// Pure helpers behind the /app2 worklist: the documented ranking (mirrored
// client-side as a defensive stable sort — the server is authoritative),
// the People/Pipes band split, and next-case selection for the 'n' hotkey.
// Unit tested in app2WorklistUtils.test.ts.

import {
  isSentinelPageCategory,
  sentinelCategoryLane,
  sentinelLaneRank,
} from './evieSentinelLabels'

const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  intervene: 3,
  watch: 2,
  info: 1,
}

/** critical=4 > intervene=3 > watch=2 > info=1 > none/unknown=0. */
export const severityRank = (severity: string | null | undefined): number =>
  (severity && SEVERITY_RANK[severity]) || 0

export interface WorklistRankable {
  applicationId: number
  topFindingSeverity?: string | null
  topFindingCategory?: string | null
  topFindingLane?: string | null
  clockDebtHours?: number | null
}

// The server sends the lane; the category fallback keeps the mirror correct
// against an older API response mid-deploy.
const laneRankOf = (item: WorklistRankable): number =>
  sentinelLaneRank(
    item.topFindingLane ?? sentinelCategoryLane(item.topFindingCategory)
  )

// Descending clock debt; the server coalesces a missing clock to zero debt,
// so a clock-less row ties with zero and sorts above negative debt (a
// deadline still ahead).
const compareClockDebtDesc = (
  a: number | null | undefined,
  b: number | null | undefined
): number => (b ?? 0) - (a ?? 0)

/**
 * The documented worklist order — mirrors the app2Worklist resolver EXACTLY,
 * because this comparator owns the worklist order the case page's 'n'
 * hotkey walks: open-finding severity desc, then lane rank
 * (applicant_waiting_on_us hottest), then clock debt desc (hours past the
 * category's SLA anchor), then applicationId desc.
 */
export const compareWorklistItems = (
  a: WorklistRankable,
  b: WorklistRankable
): number =>
  severityRank(b.topFindingSeverity) - severityRank(a.topFindingSeverity) ||
  laneRankOf(a) - laneRankOf(b) ||
  compareClockDebtDesc(a.clockDebtHours, b.clockDebtHours) ||
  b.applicationId - a.applicationId

/** Non-mutating ranked copy (server-sorted already; this is defensive). */
export const rankWorklist = <T extends WorklistRankable>(
  items: readonly T[]
): T[] => [...items].sort(compareWorklistItems)

/** Mirrors the app2Worklist resolver's default maxAgeDays cutline. */
export const DEFAULT_WORKLIST_MAX_AGE_DAYS = 21

const PEOPLE_LANES: ReadonlySet<string> = new Set([
  'applicant_waiting_on_us',
  'decision_at_risk',
  'staffs_desk',
])

export interface WorklistBandable extends WorklistRankable {
  daysSinceLastChange: number
}

export interface WorklistBands<T> {
  people: T[]
  pipes: T[]
  older: T[]
}

/**
 * The cockpit's bands: People (applicant_waiting_on_us + decision_at_risk +
 * staffs_desk) on top, Pipes (their_move + plumbing + finding-less rows)
 * beneath. With olderCutoffDays set (the "Show older" view), rows quieter
 * than the cutoff land in a third labeled band — except criticals and
 * page-grade categories, which never age out of their band, mirroring the
 * server's cutline exemption.
 */
export const splitWorklistBands = <T extends WorklistBandable>(
  items: readonly T[],
  olderCutoffDays: number | null = null
): WorklistBands<T> => {
  const bands: WorklistBands<T> = { people: [], pipes: [], older: [] }
  for (const item of items) {
    const lane =
      item.topFindingLane ?? sentinelCategoryLane(item.topFindingCategory)
    const older =
      olderCutoffDays != null &&
      item.daysSinceLastChange > olderCutoffDays &&
      item.topFindingSeverity !== 'critical' &&
      !isSentinelPageCategory(item.topFindingCategory)
    if (older) bands.older.push(item)
    else if (lane != null && PEOPLE_LANES.has(lane)) bands.people.push(item)
    else bands.pipes.push(item)
  }
  return bands
}

/**
 * The next case after currentId in the worklist order. Unknown currentId
 * falls back to the first entry (unless that IS the current case); the end
 * of the list returns null — there is deliberately no wrap-around, so 'n'
 * at the last case does nothing rather than looping the operator forever.
 */
export const nextWorklistApplicationId = (
  order: readonly number[],
  currentId: number
): number | null => {
  if (order.length === 0) return null
  const index = order.indexOf(currentId)
  if (index === -1) {
    return order[0] === currentId ? null : order[0]
  }
  return order[index + 1] ?? null
}
